import {
  Component,
  ComponentRef,
  AfterViewInit,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { SensorRecord } from "src/app/models/sensorRecord.model";
import { EventService } from "src/app/services/event.service";
import { InfoWindowComponent } from "src/app/components/info-window/info-window.component";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Loader } from "@googlemaps/js-api-loader";
import { ProgressSpinnerMode } from "@angular/material/progress-spinner";
import { ThemePalette } from "@angular/material/core";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { ChartService } from "src/app/services/chart.service";
import { TranslateService } from "@ngx-translate/core";
import { getGoogleMapsLoader } from "src/app/_helpers/google-maps-loader-helper";
declare var google: any;

@Component({
  standalone: false,
  selector: "app-node-details-map-clusterer",
  templateUrl: "./node-details-map-clusterer.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./node-details-map-clusterer.component.scss"],
})
export class NodeDetailsMapClustererComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() loading = true;
  @Input() sensorRecords: SensorRecord[] = [];
  @Input() isMobileSensor: boolean = false;
  @Input() timeCursor: number = 0;
  @Output() markerPositionChange = new EventEmitter<{ lat: number; lon: number }>();

  private static readonly MAX_MARKERS = 2000;

  markerClusterer: MarkerClusterer;
  private sharedInfoWindow: any = null;
  private lastBounds: any = null;
  private resizeObserver: ResizeObserver | null = null;
  currentMarker: any = null;
  compRef: ComponentRef<InfoWindowComponent>;
  scriptLoader!: Loader;
  options!: google.maps.MapOptions;
  nativeMap!: google.maps.Map;
  trackPolyline: any = null;
  private mapReady = false;

  @Input() spinnerColor?: ThemePalette;
  @Input() spinnerDiameter = 100;
  @Input() spinnerMode: ProgressSpinnerMode = 'indeterminate';
  @Input() spinnerStrokeWidth?: number;
  @Input() spinnerValue?: number;

  constructor(
    private eventService: EventService,
    private changeDetectorRef: ChangeDetectorRef,
    private dateFormatService: DateFormatService,
    private chartService: ChartService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
  const loader = getGoogleMapsLoader(this.translateService.currentLang);
    this.scriptLoader = loader;
    
  }

  ngAfterViewInit() {
    this.loadNodeDetailMap();
    this.loading = false;
    this.changeDetectorRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['sensorRecords'] || changes['isMobileSensor']) && this.mapReady) {
      this.updateMarkerClusterer(this.sensorRecords);
      this.drawTrackPath();
    }
  }

  loadNodeDetailMap() {
    this.scriptLoader.load().then(async (google) => {
      this.options = {
        center: { lat: 0, lng: 0 },
        zoom: 10,
        zoomControl: false,
        scaleControl: true,
        mapId: "4504f8b37365c3d0",
        mapTypeControl: true,
        mapTypeId: "roadmap",
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          mapTypeIds: ["roadmap", "hybrid", "terrain", "satellite"],
          position: google.maps.ControlPosition.TOP_RIGHT
        },
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.TOP_RIGHT
        },
        gestureHandling: "greedy",
      };
      const { Map: GoogleMap } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;

      const bounds = new google.maps.LatLngBounds();
      bounds.extend(new google.maps.LatLng({ lat: 0, lng: 0 }));
      this.options.center = bounds.getCenter().toJSON();

      this.nativeMap = new GoogleMap(
        document.getElementById("nodeDetailsMap") as HTMLElement,
        this.options
      );

      this.mapReady = true;
      this.observeMapResize();
      if (this.sensorRecords?.length) {
        this.updateMarkerClusterer(this.sensorRecords);
        this.drawTrackPath();
      }
    });
  }

  private observeMapResize(): void {
    const el = document.getElementById("nodeDetailsMap");
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    let lastWidth = el.clientWidth;
    let lastHeight = el.clientHeight;
    this.resizeObserver = new ResizeObserver(() => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width > 0 && height > 0 && (width !== lastWidth || height !== lastHeight)) {
        lastWidth = width;
        lastHeight = height;
        google.maps.event.trigger(this.nativeMap, "resize");
        if (this.lastBounds) {
          this.nativeMap.fitBounds(this.lastBounds);
          if (this.nativeMap.getZoom() > 19) {
            this.nativeMap.setZoom(19);
          }
        }
      }
    });
    this.resizeObserver.observe(el);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.compRef) {
      this.compRef.destroy();
    }
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }
    google.maps.event.clearInstanceListeners(this.nativeMap);
  }

  updateMarkerClusterer(sensorRecords: SensorRecord[]): void {
    const filteredMarkers = this.downsampleMarkers(this.filterSensorRecords(sensorRecords));
    if (!this.sharedInfoWindow) {
      this.sharedInfoWindow = new google.maps.InfoWindow();
    }
    const markers: any[] = [];
    const bounds = new google.maps.LatLngBounds();
    filteredMarkers.forEach((data) => {
      const iconColor = this.resolveMarkerColor(data);
      const marker = new google.maps.Marker({
        position: {
          lat: data.lat,
          lng: data.lon,
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: iconColor,
          fillOpacity: 1,
          scale: 8,
          strokeWeight: 0,
        },
      });
      bounds.extend(marker.getPosition());

      marker.addListener("click", () => {
        this.sharedInfoWindow.setContent(this.createInfoWindowContent(data));
        this.sharedInfoWindow.open(this.nativeMap, marker);
        this.eventService.broadcast("markerClick", data);
      });
      markers.push(marker);
    });

    if (filteredMarkers.length > 0) {
      this.lastBounds = bounds;
      this.nativeMap.fitBounds(bounds);
      if (this.nativeMap.getZoom() > 19) {
        this.nativeMap.setZoom(19);
      }
    }

    if (!this.markerClusterer) {
      this.loading = false;
      this.markerClusterer = new MarkerClusterer({
        markers: markers,
        map: this.nativeMap,
      });
    } else {
      this.markerClusterer.clearMarkers();
      this.markerClusterer.addMarkers(markers, false);
    }
  }

  private downsampleMarkers<T>(markers: T[]): T[] {
    const max = NodeDetailsMapClustererComponent.MAX_MARKERS;
    if (markers.length <= max) {
      return markers;
    }
    const step = markers.length / max;
    const sampled: T[] = [];
    for (let i = 0; i < markers.length; i += step) {
      sampled.push(markers[Math.floor(i)]);
    }
    const last = markers[markers.length - 1];
    if (sampled[sampled.length - 1] !== last) {
      sampled.push(last);
    }
    return sampled;
  }

  private resolveMarkerColor(data: any): string {
    if (typeof data?.color === 'string' && data.color.trim().length > 0) {
      return data.color;
    }

    const index = Number.isFinite(Number(data?.index)) ? Number(data.index) : 0;
    const seriesColor = this.chartService.getSeriesColor(index);
    if (seriesColor) {
      return seriesColor.toCSSHex();
    }

    return '#5B6BD5';
  }

  private createInfoWindowContent(data: SensorRecord): string {
    return `<table class="table">
              <thead>
                  <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Value</th>
                  </tr>
              </thead>
              <tbody>
                  <tr>
                      <td>Device Name</td>
                      <td>${data.deviceName || "N/A"}</td>
                  </tr>
                  <tr>
                      <td>Device ID</td>
                      <td>${data.deviceId}</td>
                  </tr>
                  <tr>
                      <td>Date&Time</td>
                      <td>${this.dateFormatService.formatTimestampToString(data.timestamp)}</td>
                  </tr>
                  <tr><td>Latitude [°]</td><td>${data.lat}</td></tr>
                  <tr><td>Longitude [°]</td><td>${data.lon}</td></tr>
              </tbody>
          </table>`;
  }

  setMarkerPosition(lat: number, lon: number): void {
    if (this.currentMarker) {
      this.currentMarker.setPosition({ lat, lng: lon });
      this.markerPositionChange.emit({ lat, lon });
    }
  }

  drawTrackPath(): void {
    this.clearTrackPath();
    if (!this.nativeMap || !this.isMobileSensor) {
      return;
    }

    const filteredRecords = this.filterSensorRecords(this.sensorRecords);
    if (filteredRecords.length < 2) {
      return;
    }

    const path = filteredRecords.map((record) => ({ lat: record.lat, lng: record.lon }));
    this.trackPolyline = new google.maps.Polyline({
      map: this.nativeMap,
      path,
      geodesic: true,
      strokeColor: '#1D4ED8',
      strokeOpacity: 0.85,
      strokeWeight: 3,
      zIndex: 2,
    });
  }

  private clearTrackPath(): void {
    if (this.trackPolyline) {
      this.trackPolyline.setMap(null);
      this.trackPolyline = null;
    }
  }

  private filterSensorRecords(sensorRecords: SensorRecord[]) {
    sensorRecords = sensorRecords.flatMap((item, parentIndex) => {
      return Array.isArray(item)
        ? item.map(value => ({ ...value, index: parentIndex }))
        : [{ ...item, index: 0 }];
    }).map((item: any) => {
      const latRaw = item.lat ?? item.latitude ?? item.Latitude;
      const lonRaw = item.lon ?? item.longitude ?? item.Longitude;
      return {
        ...item,
        lat: typeof latRaw === 'number' ? latRaw : Number(latRaw),
        lon: typeof lonRaw === 'number' ? lonRaw : Number(lonRaw)
      };
    });
    const filteredMarkers = sensorRecords.filter(({ lat, lon }) => {
      return (typeof lat === 'number' &&
        typeof lon === 'number' &&
        isFinite(lat) && isFinite(lon) &&
        !(lat === 0 && lon === 0) &&
        lon >= -180 && lon <= 180 &&
        lat >= -90 && lat <= 90);
    });

    // For mobile sensors, keep all positions (don't deduplicate)
    // For stationary sensors, deduplicate by lat/lon
    if (this.isMobileSensor) {
      return filteredMarkers; // Keep all positions
    }

    const markerMap = new Map();
    filteredMarkers.forEach((marker) => {
      markerMap.set(`${marker.lon}:${marker.lat}`, marker);
    });

    return Array.from(markerMap.values());
  }
}